import React, { useEffect, useMemo, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { tallyOpeningApi, type TallyOpeningLedger, type TallyOpeningState } from "../../data/tallyOpeningApi";
import {
  buildTallyOpeningSections,
  type TallyOpeningNode,
  type TallyOpeningSection,
} from "../../domain/tallyChart";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { cn } from "../../components/cn";
import { ChevronDownIcon, ChevronRightIcon } from "../../components/icons";

/**
 * Opening balances laid out the way the Zoho screen lays them out: one
 * accordion per primary group, debit and credit columns, a running total.
 * Inside a group, Tally's own nesting stays — reserved subgroup, then a
 * customer-created group, then the ledger the figure is typed on.
 */

type Side = "debit" | "credit";
type Draft = Record<string, { side: Side | null; amount: string }>;

const AMOUNT_GRID =
  "grid-cols-[minmax(6rem,1fr)_minmax(10rem,10.5rem)_minmax(10rem,10.5rem)] " +
  "min-[1366px]:grid-cols-[minmax(6rem,1fr)_12rem_12rem]";
const AMOUNT_GRID_FLOOR = "min-w-[28.5rem]";
const AMOUNT_TEXT = "text-xs tabular-nums min-[1366px]:text-sm";

const MAX_INTEGER_DIGITS = 13;
const MAX_DECIMALS = 2;

function draftFrom(state: TallyOpeningState): Draft {
  const out: Draft = {};
  for (const row of state.ledgers) {
    if (row.is_group) continue;
    out[row.guid] = {
      side: row.side,
      amount: row.side === null ? "" : String(row.amount),
    };
  }
  return out;
}

function amountError(text: string): string | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;

  const inner = trimmed.startsWith("(") && trimmed.endsWith(")") ? trimmed.slice(1, -1) : trimmed;
  const clean = inner.replace(/[^0-9.-]/g, "").replace(/^-/, "");
  if (clean === "" || !/^\d*(\.\d*)?$/.test(clean)) return "That is not an amount.";

  const [whole = "", decimals = ""] = clean.split(".");
  if (whole.replace(/^0+/, "").length > MAX_INTEGER_DIGITS) {
    return `Amount too large — up to ${MAX_INTEGER_DIGITS} digits before the decimal`;
  }
  if (decimals.length > MAX_DECIMALS) return `Up to ${MAX_DECIMALS} decimal places`;
  return null;
}

function formatAmount(value: number): string {
  return value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseAmount(text: string): number {
  const trimmed = text.trim();
  if (trimmed === "") return 0;
  const negative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const value = Number(trimmed.replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(value)) return 0;
  return negative ? -Math.abs(value) : value;
}

/** Debits positive, credits negative. A negative typed in one column is the other side. */
function signedValue(side: Side | null, amount: string): number {
  if (side === null) return 0;
  const value = parseAmount(amount);
  if (value === 0) return 0;
  const effective: Side = value < 0 ? (side === "debit" ? "credit" : "debit") : side;
  return (effective === "credit" ? -1 : 1) * Math.abs(value);
}

function totalsFrom(draft: Draft): { debit: number; credit: number; difference: number; balanced: boolean } {
  let debit = 0;
  let credit = 0;
  for (const cell of Object.values(draft)) {
    if (amountError(cell.amount) !== null) continue;
    const value = signedValue(cell.side, cell.amount);
    if (value > 0) debit += value;
    else credit += -value;
  }
  const difference = Math.round((debit - credit) * 100) / 100;
  return { debit, credit, difference, balanced: Math.abs(difference) < 0.005 };
}

function subtreeSigned(node: TallyOpeningNode, draft: Draft): number {
  if (!node.row.is_group) return signedValue(draft[node.row.guid]?.side ?? null, draft[node.row.guid]?.amount ?? "");
  return node.children.reduce((sum, child) => sum + subtreeSigned(child, draft), 0);
}

function subtreeHasFigure(node: TallyOpeningNode, draft: Draft): boolean {
  if (!node.row.is_group) return Math.abs(signedValue(draft[node.row.guid]?.side ?? null, draft[node.row.guid]?.amount ?? "")) >= 0.005;
  return node.children.some((child) => subtreeHasFigure(child, draft));
}

function openWithFigures(sections: TallyOpeningSection[], draft: Draft): Record<string, boolean> {
  const open: Record<string, boolean> = {};
  const walk = (nodes: TallyOpeningNode[]): boolean => {
    let any = false;
    for (const node of nodes) {
      if (!node.row.is_group) {
        if (subtreeHasFigure(node, draft)) any = true;
        continue;
      }
      if (walk(node.children)) {
        open[node.row.guid] = true;
        any = true;
      }
    }
    return any;
  };
  for (const section of sections) {
    if (walk(section.nodes)) open[section.key] = true;
  }
  return open;
}

function lineFromCell(guid: string, cell: { side: Side | null; amount: string }) {
  const trimmed = cell.amount.trim();
  const value = trimmed === "" || cell.side === null ? 0 : parseAmount(trimmed);
  if (value === 0) return { guid, debit: 0, credit: null };
  const side: Side = value < 0 ? (cell.side === "debit" ? "credit" : "debit") : (cell.side ?? "debit");
  const amount = Math.abs(value);
  return side === "credit"
    ? { guid, debit: null, credit: amount }
    : { guid, debit: amount, credit: null };
}

export function TallyOpeningBalanceGrid({
  clientId,
  reloadToken = 0,
  onBalancedChange,
  beforeGroups,
}: {
  clientId: ClientId;
  reloadToken?: number;
  onBalancedChange?: (balanced: boolean) => void;
  beforeGroups?: (locked: boolean) => React.ReactNode;
}) {
  const [state, setState] = useState<TallyOpeningState | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const dirty = useRef(new Set<string>());

  useEffect(() => {
    let live = true;
    void tallyOpeningApi.state(clientId).then(
      (next) => {
        if (!live) return;
        dirty.current.clear();
        const nextDraft = draftFrom(next);
        setState(next);
        setDraft(nextDraft);
        setOpen(openWithFigures(buildTallyOpeningSections(next.ledgers), nextDraft));
        setLoadError(null);
        setLoading(false);
      },
      (reason: unknown) => {
        if (!live) return;
        setLoadError(reason instanceof Error ? reason.message : "Could not load the opening balances.");
        setLoading(false);
      }
    );
    return () => {
      live = false;
    };
  }, [clientId, reloadToken]);

  useEffect(() => {
    if (state?.push.status !== "pending") return undefined;
    const timer = window.setInterval(() => {
      void tallyOpeningApi.state(clientId).then((next) => {
        setState(next);
        if (next.push.status !== "pending") {
          dirty.current.clear();
          setDraft(draftFrom(next));
        }
      }).catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [clientId, state?.push.status]);

  const sections = useMemo(() => buildTallyOpeningSections(state?.ledgers ?? []), [state]);
  const totals = useMemo(() => totalsFrom(draft), [draft]);
  const refusedCells = useMemo(
    () => Object.values(draft).filter((cell) => amountError(cell.amount) !== null).length,
    [draft]
  );

  useEffect(() => {
    onBalancedChange?.(totals.balanced && refusedCells === 0);
  }, [totals.balanced, refusedCells, onBalancedChange]);

  const sending = state?.push.status === "pending";
  const locked = pushing || sending;

  function edit(guid: string, side: Side, amount: string) {
    dirty.current.add(guid);
    setDraft((current) => ({ ...current, [guid]: { side, amount } }));
    setSaveError(null);
  }

  async function commit(guid: string) {
    if (!dirty.current.has(guid)) return;
    const cell = draft[guid];
    if (cell === undefined || amountError(cell.amount) !== null) return;
    try {
      const next = await tallyOpeningApi.save(clientId, [lineFromCell(guid, cell)]);
      dirty.current.delete(guid);
      setState(next);
      setSaveError(null);
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : "Could not save that figure.");
    }
  }

  async function send() {
    setPushing(true);
    setSaveError(null);
    try {
      // Push only sends rows that have been saved. Persist the whole draft
      // first so a figure left as Tally reported it still goes out.
      const lines = Object.entries(draft)
        .filter(([guid, cell]) => {
          if (amountError(cell.amount) !== null) return false;
          // A blank that was never edited is not a zero to write. A blank the
          // CA cleared is, and so is every figure still sitting in a box.
          if (cell.side !== null && cell.amount.trim() !== "") return true;
          return dirty.current.has(guid);
        })
        .map(([guid, cell]) => lineFromCell(guid, cell));
      const saved = await tallyOpeningApi.save(clientId, lines);
      setState(saved);
      setDraft(draftFrom(saved));
      const next = await tallyOpeningApi.push(clientId);
      dirty.current.clear();
      setState(next);
      setDraft(draftFrom(next));
    } catch (reason) {
      setSaveError(reason instanceof Error ? reason.message : "Could not send the opening balances.");
    } finally {
      setPushing(false);
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading the chart of accounts…</p>;
  if (loadError !== null || state === null) {
    return <Callout variant="warning">{loadError ?? "Could not load the opening balances."}</Callout>;
  }

  function toggle(key: string) {
    setOpen((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <div className="flex flex-col gap-4">
      {saveError !== null && <Callout variant="warning">{saveError}</Callout>}
      {state.push.error ? <Callout variant="warning">{state.push.error}</Callout> : null}

      <div className="overflow-x-auto">
        <div className={cn("flex flex-col gap-4", AMOUNT_GRID_FLOOR)}>
          <div className={cn("grid gap-2 px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground", AMOUNT_GRID)}>
            <span>Account</span>
            <span className="text-right">Debit</span>
            <span className="text-right">Credit</span>
          </div>

          {beforeGroups?.(locked)}

          {state.ledgers.every((row) => row.is_group) || state.ledgers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No ledgers have been read from Tally yet.</p>
          ) : (
            sections.map((section) => (
              <SectionBlock
                key={section.key}
                section={section}
                open={open[section.key] === true}
                onToggle={() => toggle(section.key)}
                draft={draft}
                expanded={open}
                onToggleNode={toggle}
                locked={locked}
                focusKey={focusKey}
                onFocus={setFocusKey}
                onChange={edit}
                onCommit={(guid) => void commit(guid)}
              />
            ))
          )}

          <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3">
            <div className={cn("grid gap-2 text-sm font-medium", AMOUNT_GRID)}>
              <span>Total</span>
              <span className={cn("text-right", AMOUNT_TEXT)}>{formatAmount(totals.debit)}</span>
              <span className={cn("text-right", AMOUNT_TEXT)}>{formatAmount(totals.credit)}</span>
            </div>
            {refusedCells > 0 ? (
              <p className="text-sm text-destructive">
                {refusedCells === 1
                  ? "One figure has not been accepted. Fix it before sending — it is not saved, and not in the total."
                  : `${refusedCells} figures have not been accepted. Fix them before sending — they are not saved, and not in the total.`}
              </p>
            ) : totals.balanced ? (
              <p className="text-sm text-emerald-700 dark:text-emerald-400">Debits and credits agree.</p>
            ) : (
              <p className="text-sm text-amber-800 dark:text-amber-400">
                Difference {formatAmount(Math.abs(totals.difference))}. Debits and credits must agree before this
                can be sent.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {sending
            ? "Sending to Tally…"
            : state.push.status === "done"
              ? "Tally already holds these opening balances. Sending replaces them."
              : "Nothing has been sent to Tally yet."}
        </p>
        <CoButton
          onClick={() => void send()}
          disabled={locked || !totals.balanced || refusedCells > 0}
          className="w-fit"
        >
          {sending || pushing ? "Sending…" : state.push.status === "done" ? "Replace in Tally" : "Send to Tally"}
        </CoButton>
      </div>
    </div>
  );
}

function SectionBlock({
  section,
  open,
  onToggle,
  draft,
  expanded,
  onToggleNode,
  locked,
  focusKey,
  onFocus,
  onChange,
  onCommit,
}: {
  section: TallyOpeningSection;
  open: boolean;
  onToggle: () => void;
  draft: Draft;
  expanded: Record<string, boolean>;
  onToggleNode: (key: string) => void;
  locked: boolean;
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onChange: (guid: string, side: Side, amount: string) => void;
  onCommit: (guid: string) => void;
}) {
  const groupTotal = section.nodes.reduce((sum, node) => sum + subtreeSigned(node, draft), 0);

  return (
    <section className="rounded-md border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
      >
        {open ? (
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">{section.label}</span>
        <span className="text-xs text-muted-foreground">{section.leaves}</span>
        {Math.abs(groupTotal) >= 0.005 && (
          <span className="ml-auto text-sm tabular-nums text-muted-foreground">
            {formatAmount(Math.abs(groupTotal))} {groupTotal < 0 ? "Cr" : "Dr"}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t">
          {section.nodes.map((node, index) => (
            <OpeningBranch
              key={node.row.guid}
              node={node}
              depth={0}
              first={index === 0}
              draft={draft}
              expanded={expanded}
              onToggle={onToggleNode}
              locked={locked}
              focusKey={focusKey}
              onFocus={onFocus}
              onChange={onChange}
              onCommit={onCommit}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function OpeningBranch({
  node,
  depth,
  first,
  draft,
  expanded,
  onToggle,
  locked,
  focusKey,
  onFocus,
  onChange,
  onCommit,
}: {
  node: TallyOpeningNode;
  depth: number;
  first: boolean;
  draft: Draft;
  expanded: Record<string, boolean>;
  onToggle: (key: string) => void;
  locked: boolean;
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onChange: (guid: string, side: Side, amount: string) => void;
  onCommit: (guid: string) => void;
}) {
  if (!node.row.is_group) {
    return (
      <LedgerRow
        row={node.row}
        depth={depth}
        className={first ? undefined : "border-t"}
        draft={draft}
        locked={locked}
        focusKey={focusKey}
        onFocus={onFocus}
        onChange={onChange}
        onCommit={onCommit}
      />
    );
  }

  const isOpen = expanded[node.row.guid] === true;
  const total = subtreeSigned(node, draft);
  const leaves = countGroupLeaves(node);

  return (
    <div className={first ? undefined : "border-t"}>
      <button
        type="button"
        onClick={() => onToggle(node.row.guid)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2 py-2 pr-3 text-left hover:bg-muted/40"
        style={{ paddingLeft: 12 + depth * 16 }}
      >
        {isOpen ? (
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">{node.row.name}</span>
        <span className="text-xs text-muted-foreground">{leaves}</span>
        {Math.abs(total) >= 0.005 && (
          <span className="ml-auto text-sm tabular-nums text-muted-foreground">
            {formatAmount(Math.abs(total))} {total < 0 ? "Cr" : "Dr"}
          </span>
        )}
      </button>
      {isOpen &&
        node.children.map((child) => (
          <OpeningBranch
            key={child.row.guid}
            node={child}
            depth={depth + 1}
            first={false}
            draft={draft}
            expanded={expanded}
            onToggle={onToggle}
            locked={locked}
            focusKey={focusKey}
            onFocus={onFocus}
            onChange={onChange}
            onCommit={onCommit}
          />
        ))}
    </div>
  );
}

function countGroupLeaves(node: TallyOpeningNode): number {
  return node.children.reduce((count, child) => count + (child.row.is_group ? countGroupLeaves(child) : 1), 0);
}

function LedgerRow({
  row,
  depth,
  className,
  draft,
  locked,
  focusKey,
  onFocus,
  onChange,
  onCommit,
}: {
  row: TallyOpeningLedger;
  depth: number;
  className?: string;
  draft: Draft;
  locked: boolean;
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onChange: (guid: string, side: Side, amount: string) => void;
  onCommit: (guid: string) => void;
}) {
  const cell = draft[row.guid];
  const refusal = amountError(cell?.amount ?? "");

  return (
    <div className={cn("grid items-center gap-2 px-3 py-1.5", AMOUNT_GRID, className)}>
      <div className="min-w-0" style={depth > 0 ? { paddingLeft: depth * 16 } : undefined}>
        <span className="block truncate text-sm" title={row.name}>
          {row.name}
        </span>
        {refusal !== null ? (
          <span className="text-xs text-destructive">{refusal}</span>
        ) : row.error ? (
          <span className="text-xs text-destructive">{row.error}</span>
        ) : row.tally_side !== null ? (
          <span className="text-xs text-muted-foreground">
            In Tally: {formatAmount(row.tally_amount)} {row.tally_side === "credit" ? "Cr" : "Dr"}
          </span>
        ) : null}
      </div>
      <AmountCell row={row} side="debit" draft={draft} locked={locked} focusKey={focusKey} onFocus={onFocus} onChange={onChange} onCommit={onCommit} />
      <AmountCell row={row} side="credit" draft={draft} locked={locked} focusKey={focusKey} onFocus={onFocus} onChange={onChange} onCommit={onCommit} />
    </div>
  );
}

function AmountCell({
  row,
  side,
  draft,
  locked,
  focusKey,
  onFocus,
  onChange,
  onCommit,
}: {
  row: TallyOpeningLedger;
  side: Side;
  draft: Draft;
  locked: boolean;
  focusKey: string | null;
  onFocus: (key: string | null) => void;
  onChange: (guid: string, side: Side, amount: string) => void;
  onCommit: (guid: string) => void;
}) {
  const cell = draft[row.guid];
  const key = `${row.guid}:${side}`;
  const focused = focusKey === key;
  const raw = cell?.side === side ? cell.amount : "";
  const parsed = parseAmount(raw);
  const refused = amountError(raw) !== null;
  const display = focused || refused || raw === "" || parsed === 0 ? raw : formatAmount(Math.abs(parsed));

  return (
    <input
      inputMode="decimal"
      value={display}
      disabled={locked}
      aria-label={`${row.name} ${side}`}
      aria-invalid={refused || undefined}
      onFocus={() => onFocus(key)}
      onChange={(event) => onChange(row.guid, side, event.target.value)}
      onBlur={() => {
        onFocus(null);
        onCommit(row.guid);
      }}
      className={cn(
        "h-8 w-full rounded-md border bg-background px-2 text-right outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50",
        refused && "border-destructive text-destructive focus-visible:ring-destructive/50",
        AMOUNT_TEXT
      )}
    />
  );
}
